'use client';
import { useState, useRef } from 'react';
import type { NavigationGroup } from '@/components/Header/links';
import type { TFeaturedResource } from '@/components/Header/featured.types';
import NavFeatured from '@/components/Header/components/NavFeatured';
import Link from 'next/link';
import {
  ChevronDown,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  ArrowRightLong
} from '@baseline/ui';
import commonStyles from '../../navigationMenu.module.scss';
import styles from './navigationSubMenu.module.scss';

const MenuLinkSection = ({
  sections,
  featured,
  onClick
}: {
  sections: NavigationGroup['sections'];
  featured?: TFeaturedResource;
  onClick?: () => void;
}) => {
  return (
    <div className={styles.sectionsWrap}>
      {(sections ?? []).map((section) => (
        <div className={styles.section} key={section.label}>
          {section.label && (
            <div className={styles.sectionLabel}>{section.label}</div>
          )}

          <nav className={styles.itemsWrap}>
            {section.items.map((item) => (
              <Link
                onClick={onClick}
                key={item.href ?? item.title}
                className={styles.sublink}
                href={item.href}
                target={item.newTab ? '_blank' : undefined}
                rel={item.newTab ? 'noopener noreferrer' : undefined}
              >
                <div className={styles.sublinkGroup}>
                  <span className={styles.sublinkTitle}>{item.title}</span>
                  {item.description ? (
                    <span className={styles.sublinkDescription}>
                      {item.description}
                    </span>
                  ) : null}
                </div>
                <ArrowRightLong className={styles.endIconBubble} />
              </Link>
            ))}
          </nav>
        </div>
      ))}

      {featured ? (
        <div className={styles.featuredColumn}>
          <NavFeatured resource={featured} onNavigate={onClick} />
        </div>
      ) : null}
    </div>
  );
};

export default function NavigationSubMenu({
  title,
  sections,
  featured
}: Readonly<{
  title: string;
  sections: NavigationGroup['sections'];
  featured?: TFeaturedResource;
}>) {
  const [isOpen, setIsOpen] = useState(false);
  const [preventTriggerFocus, setPreventTriggerFocus] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    setIsOpen(true);
    setPreventTriggerFocus(false);
  };

  const handleMouseLeave = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setIsOpen(false);
      setPreventTriggerFocus(true);
    }, 100);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div className={styles.popoverAnchor}></div>
      </PopoverAnchor>
      <PopoverTrigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          setPreventTriggerFocus(false);
        }}
      >
        <button
          className={`${commonStyles.link} ${commonStyles.linkHover} ${styles.trigger}`}
        >
          {title} <ChevronDown />
        </button>
      </PopoverTrigger>

      <PopoverContent
        sideOffset={30}
        className={styles.popoverContent}
        onCloseAutoFocus={(e) => {
          if (preventTriggerFocus) {
            e.preventDefault();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MenuLinkSection
          sections={sections}
          featured={featured}
          onClick={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
